import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, MousePointerClick, Users, CreditCard, Share2, Heart, Lock, Mail, Eye, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { fmtPoints } from "../data/mock";

const RANGES = [7, 30, 90, 365];

function Stat({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icon className="h-4 w-4 text-blue-600" /> {label}</div>
      <p className="mt-2 text-3xl font-extrabold text-slate-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function RankTable({ title, rows, keyLabel, testId }) {
  const max = Math.max(...rows.map((r) => r.clicks), 1);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <h2 className="text-base font-bold text-slate-900">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No clicks yet in this period.</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {rows.map((r, i) => (
            <li key={r.key} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 font-semibold text-slate-900"><span className="w-5 text-xs text-slate-400">{i + 1}.</span>{r.key}</span>
                <span className="text-slate-600"><b className="text-slate-900">{r.clicks}</b> click{r.clicks === 1 ? "" : "s"} · avg {fmtPoints(r.avgPoints)} pts</span>
              </div>
              <div className="ml-7 mt-1.5 h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-blue-500" style={{ width: `${(r.clicks / max) * 100}%` }} /></div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function DigestCard() {
  const { toast } = useToast();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const show = async () => { setPreview(await api.digestPreview()); };
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.digestSendNow();
      toast(r.sent ? { title: "Digest sent", description: "Check your inbox." } : { title: "Digest not sent", description: r.reason || "Email is not configured yet.", variant: "destructive" });
    } catch { toast({ title: "Could not send digest", variant: "destructive" }); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5" data-testid="digest-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-bold text-slate-900"><Mail className="h-4 w-4 text-blue-600" /> Monday weekly digest</p>
          <p className="mt-1 text-xs text-slate-600">Every Monday morning we email admins the week's top-clicked routes, top programs, sign-ups and checkouts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={show} data-testid="digest-preview-button" className="gap-1.5 bg-white"><Eye className="h-4 w-4" /> Preview</Button>
          <Button size="sm" onClick={send} disabled={busy} data-testid="digest-send-button" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Mail className="h-4 w-4" /> Email me now</Button>
        </div>
      </div>
      {preview && (
        <div className="relative mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="digest-preview">
          <button onClick={() => setPreview(null)} aria-label="Close preview" className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow hover:text-slate-900"><X className="h-4 w-4" /></button>
          <iframe title="Weekly digest preview" srcDoc={preview} className="h-[560px] w-full" sandbox="" />
        </div>
      )}
    </div>
  );
}

export default function AdminInsights() {
  const { user, loading } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.isAdmin) return;
    setData(null);
    api.adminInsights(days).then(setData).catch((e) => setError(e?.response?.data?.detail || "Could not load insights"));
  }, [user, days]);

  if (loading) return <main className="min-h-[70vh] bg-slate-50" />;
  if (!user?.isAdmin) {
    return (
      <main className="min-h-[70vh] bg-slate-50">
        <div className="mx-auto max-w-md px-5 py-24 text-center" data-testid="insights-forbidden">
          <Lock className="mx-auto h-10 w-10 text-slate-300" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Owner access only</h1>
          <p className="mt-2 text-slate-600">Sign in with the admin account to view FindMyFlight insights.</p>
          <Link to="/login" className="mt-6 inline-block"><Button className="bg-blue-600 hover:bg-blue-700">Sign in</Button></Link>
        </div>
      </main>
    );
  }

  const t = data?.totals;
  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Insights | FindMyFlight" description="Owner dashboard." path="/admin/insights" />
      <div className="mx-auto max-w-6xl px-5 py-10" data-testid="insights-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-blue-600"><BarChart3 className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Insights</span></div>
            <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Book clicks & conversion</h1>
            <p className="mt-2 text-slate-600">Which deals send people to airlines, and how the funnel is converting.</p>
          </div>
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setDays(r)} data-testid={`range-${r}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${days === r ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>{r === 365 ? "1 year" : `${r} days`}</button>
            ))}
          </div>
        </div>

        <div className="mt-8"><DigestCard /></div>
        {error && <p className="mt-6 text-sm text-red-600">{error}</p>}
        {!data && !error && <p className="mt-10 text-center text-slate-500">Loading…</p>}
        {t && (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Stat icon={MousePointerClick} label="Book clicks" value={t.bookClicks} sub={`${t.airlineClicks} airline · ${t.googleFlightsClicks} Google Flights`} testId="stat-clicks" />
              <Stat icon={Users} label="Sign-ups" value={t.signups} sub={`${t.proUsers} PRO members total`} testId="stat-signups" />
              <Stat icon={CreditCard} label="Checkouts" value={`${t.checkoutsPaid}/${t.checkoutsStarted}`} sub={`${t.conversionRate}% paid`} testId="stat-checkouts" />
              <Stat icon={Share2} label="Shares · Saves" value={`${t.dealsShared} · ${t.dealsSaved}`} sub="deals shared · saved to wishlists" testId="stat-engagement" />
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <RankTable title="Top-converting routes" rows={data.topRoutes} testId="top-routes" />
              <RankTable title="Top programs" rows={data.topPrograms} testId="top-programs" />
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="recent-clicks">
              <h2 className="text-base font-bold text-slate-900">Recent clicks</h2>
              {data.recent.length === 0 ? <p className="mt-3 text-sm text-slate-500">Nothing yet — clicks appear here the moment someone taps "Book on…".</p> : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs uppercase text-slate-500"><tr><th className="py-2 pr-4">When</th><th className="py-2 pr-4">Route</th><th className="py-2 pr-4">Program</th><th className="py-2 pr-4">Cabin</th><th className="py-2 pr-4">Points</th><th className="py-2">Target</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.recent.map((r) => (
                        <tr key={r.id}><td className="py-2 pr-4 text-slate-500">{new Date(r.createdAt).toLocaleString()}</td><td className="py-2 pr-4 font-semibold">{r.route}</td><td className="py-2 pr-4">{r.program}</td><td className="py-2 pr-4 capitalize">{r.cabin}</td><td className="py-2 pr-4">{fmtPoints(r.points)}</td><td className="py-2 capitalize">{r.target.replace("_", " ")}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <p className="mt-4 flex items-center gap-1 text-xs text-slate-400"><Heart className="h-3 w-3" /> Top routes rank by clicks to airline booking pages — your best signal of real booking intent.</p>
          </>
        )}
      </div>
    </main>
  );
}
